/**
 * The axis profiles: every combination of axis values CLUE creates documents at.
 *
 * A profile is a named bundle of positions on the axes — who owns a document, where it is kept, what it
 * is about, whether several people may write it (see docs/document-axes/axes.md). It is the thing people
 * already mean by "a class-wide document" or "a problem document": a shorthand for a set of coordinates
 * that no single stored field holds.
 *
 * Profiles are why a unit may add kinds but may not add axis combinations, which is what lets `kind` stay
 * open-ended while the set of combinations stays closed. A unit config may declare a new kind, but every
 * kind is registered against one of the profiles below, so a configuration can add a document to an
 * existing combination and cannot invent a new one. This file is therefore the complete list of axis
 * combinations the application supports — the one place to look to check that they are all intended and
 * all handled.
 *
 * | profile        | owner | container | curriculum | concurrent | kinds today                                    |
 * |----------------|-------|-----------|------------|------------|------------------------------------------------|
 * | `problemLike`  | user  | offering  | problem    | no         | problem, planning, problemPublication, support  |
 * | `personalLike` | user  | class     | none       | no         | personal, learningLog, and their publications   |
 * | `group`        | group | offering  | problem    | yes        | group                                           |
 * | `classWide`    | class | classUnit | unit       | yes        | whatever units declare (driving question board) |
 *
 * The curriculum column is not declared here: `containerType` fixes it, because every container above the
 * class is identified by a curriculum coordinate (see getDocumentLocationFields).
 */
/** A user's own work on one assigned problem, and the read-only publications made from it. */
export const kProblemLikeProfile = {
    name: "problemLike",
    ownerType: "user",
    containerType: "offering"
};
/** A user's own work that follows them between assignments, about no particular content. */
export const kPersonalLikeProfile = {
    name: "personalLike",
    ownerType: "user",
    containerType: "class"
};
/**
 * One group's shared document for one assignment. Kept in the offering like the problem documents beside
 * it; what makes it the group's is its owner.
 */
export const kGroupProfile = {
    name: "group",
    ownerType: "group",
    containerType: "offering",
    concurrent: true
};
/**
 * One document shared by a whole class for a whole unit. Differs from `group` on owner, container, and
 * curriculum alone — which is the demonstration that the axes, not the type, decide how a document behaves.
 */
export const kClassWideProfile = {
    name: "classWide",
    ownerType: "class",
    containerType: "classUnit",
    concurrent: true
};
/** Every profile, for enumeration — tests, tooling, and anything auditing the set as a whole. */
export const kDocumentAxisProfiles = [
    kProblemLikeProfile, kPersonalLikeProfile, kGroupProfile, kClassWideProfile
];
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZG9jdW1lbnQtYXhpcy1wcm9maWxlcy5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uLy4uL3NyYy9tb2RlbHMvZG9jdW1lbnQvZG9jdW1lbnQtYXhpcy1wcm9maWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBd0JHO0FBbUNILDhGQUE4RjtBQUM5RixNQUFNLENBQUMsTUFBTSxtQkFBbUIsR0FBeUI7SUFDdkQsSUFBSSxFQUFFLGFBQWE7SUFDbkIsU0FBUyxFQUFFLE1BQU07SUFDakIsYUFBYSxFQUFFLFVBQVU7Q0FDMUIsQ0FBQztBQUVGLDRGQUE0RjtBQUM1RixNQUFNLENBQUMsTUFBTSxvQkFBb0IsR0FBeUI7SUFDeEQsSUFBSSxFQUFFLGNBQWM7SUFDcEIsU0FBUyxFQUFFLE1BQU07SUFDakIsYUFBYSxFQUFFLE9BQU87Q0FDdkIsQ0FBQztBQUVGOzs7R0FHRztBQUNILE1BQU0sQ0FBQyxNQUFNLGFBQWEsR0FBeUI7SUFDakQsSUFBSSxFQUFFLE9BQU87SUFDYixTQUFTLEVBQUUsT0FBTztJQUNsQixhQUFhLEVBQUUsVUFBVTtJQUN6QixVQUFVLEVBQUUsSUFBSTtDQUNqQixDQUFDO0FBRUY7OztHQUdHO0FBQ0gsTUFBTSxDQUFDLE1BQU0saUJBQWlCLEdBQXlCO0lBQ3JELElBQUksRUFBRSxXQUFXO0lBQ2pCLFNBQVMsRUFBRSxPQUFPO0lBQ2xCLGFBQWEsRUFBRSxXQUFXO0lBQzFCLFVBQVUsRUFBRSxJQUFJO0NBQ2pCLENBQUM7QUFFRixpR0FBaUc7QUFDakcsTUFBTSxDQUFDLE1BQU0scUJBQXFCLEdBQW9DO0lBQ3BFLG1CQUFtQixFQUFFLG9CQUFvQixFQUFFLGFBQWEsRUFBRSxpQkFBaUI7Q0FDNUUsQ0FBQyJ9